import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { authApi } from '@/lib/api'
import { AxiosError } from 'axios'

type VerificationState = 'loading' | 'success' | 'error' | 'expired'

interface ApiErrorResponse {
  success: boolean
  error?: {
    code?: string
    message?: string
  }
}

export function MagicLinkPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { verifyMagicLink } = useAuth()
  const [state, setState] = useState<VerificationState>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [resendEmail, setResendEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  const token = searchParams.get('token')

  useEffect(() => {
    async function verifyLink() {
      if (!token) {
        setState('error')
        setErrorMessage('No magic link token provided.')
        return
      }

      try {
        await verifyMagicLink(token)
        setState('success')
        // Auto-redirect to feed after 2 seconds
        setTimeout(() => {
          navigate('/feed')
        }, 2000)
      } catch (error) {
        const axiosError = error as AxiosError<ApiErrorResponse>
        const errorCode = axiosError.response?.data?.error?.code
        const message = axiosError.response?.data?.error?.message || 'Login failed'

        if (errorCode === 'TOKEN_EXPIRED' || message.includes('expired')) {
          setState('expired')
          setErrorMessage('This magic link has expired. Please request a new one.')
        } else if (errorCode === 'TOKEN_INVALID' || message.includes('invalid')) {
          setState('error')
          setErrorMessage('This magic link is invalid or has already been used.')
        } else {
          setState('error')
          setErrorMessage(message)
        }
      }
    }

    verifyLink()
  }, [token, verifyMagicLink, navigate])

  const handleResendMagicLink = async () => {
    if (!resendEmail) return

    setResendLoading(true)
    try {
      await authApi.sendMagicLink(resendEmail)
      setResendSuccess(true)
    } catch {
      setErrorMessage('Failed to send magic link. Please try again.')
    } finally {
      setResendLoading(false)
    }
  }

  if (state === 'loading') {
    return (
      <Card className="border-0 shadow-none lg:border lg:shadow-sm">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="font-display text-2xl">Signing you in</CardTitle>
          <CardDescription>Please wait while we verify your magic link...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </CardContent>
      </Card>
    )
  }

  if (state === 'success') {
    return (
      <Card className="border-0 shadow-none lg:border lg:shadow-sm">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="font-display text-2xl">Welcome back!</CardTitle>
          <CardDescription>
            You&apos;ve been signed in successfully. Redirecting to your feed...
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Button onClick={() => navigate('/feed')}>
            Go to Feed
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (state === 'expired' || state === 'error') {
    return (
      <Card className="border-0 shadow-none lg:border lg:shadow-sm">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4">
            <XCircle className="h-16 w-16 text-destructive" />
          </div>
          <CardTitle className="font-display text-2xl">
            {state === 'expired' ? 'Link Expired' : 'Login Failed'}
          </CardTitle>
          <CardDescription>{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {resendSuccess ? (
            <div className="text-center py-4">
              <Mail className="mx-auto h-12 w-12 text-primary" />
              <p className="mt-4 text-sm text-muted-foreground">
                A new magic link has been sent. Please check your inbox.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-center text-sm text-muted-foreground">
                Enter your email to receive a new magic link:
              </p>
              <input
                type="email"
                placeholder="your@email.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <Button
                className="w-full"
                onClick={handleResendMagicLink}
                disabled={resendLoading || !resendEmail}
              >
                {resendLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send New Magic Link
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <p className="text-center text-sm text-muted-foreground">
            Prefer to use a password?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    )
  }

  return null
}

export default MagicLinkPage
